import {
  GenericPeriod,
  PeriodType,
  PeriodTypeQuery,
  StandardInputType,
} from "@/types"
import { formatPeriod, parseISOStringDate } from "./date"
import { TimeframeSettings } from "@/feature/financial-model/settings/store/financialModelSettingsStore"

export const getIsLastHistoricalPeriod = (
  queryPeriod: PeriodTypeQuery,
  data: { period_type: PeriodType; id: string }[]
) => {
  let lastHistoricalPeriod = ``
  data.forEach((item, index) => {
    if (item.period_type === `historical`) {
      if (
        queryPeriod === `all` &&
        data[index + 1]?.period_type !== `historical`
      ) {
        lastHistoricalPeriod = item.id
      }
    }
  })
  return { lastHistoricalPeriod }
}

export interface PeriodExtractor<T extends GenericPeriod> {
  getFiscalYear: (item: T) => number | null
  getPeriod: (item: T) => string
  getFiscalMonth: (item: T) => number | null
  getPeriodType: (item: T) => PeriodType
}

export const defaultPeriodExtractor: PeriodExtractor<GenericPeriod> = {
  getFiscalYear: (item) => item.fiscal_year ?? null,
  getPeriod: (item) => item.period,
  getFiscalMonth: (item) => item.fiscal_month ?? null,
  getPeriodType: (item) => item.data_type ?? item.type ?? item.period_type!,
}

export const groupDataByYear = <T extends GenericPeriod>(
  data: T[],
  extractor?: PeriodExtractor<GenericPeriod>
) => {
  const periodExtractor = extractor ?? defaultPeriodExtractor
  const dataGroupedByYear = data.reduce(
    (acc, item) => {
      const fiscalYear = periodExtractor.getFiscalYear(item)
      if (fiscalYear && !acc[fiscalYear]) {
        acc[fiscalYear] = []
      }
      if (fiscalYear) {
        acc[fiscalYear].push(item)
      }
      return acc
    },
    {} as Record<number, T[]>
  )

  const years = Object.keys(dataGroupedByYear)
    .map(Number)
    .sort((a, b) => a - b)
    .map((year) => ({
      year,
      span: dataGroupedByYear[year].length,
    }))

  const availableYears = years.map((year) => year.year)

  const months = data.map((item) => ({
    id: periodExtractor.getPeriod(item),
    month: periodExtractor.getFiscalMonth(item),
    period: formatPeriod(periodExtractor.getPeriod(item)),
    period_type: periodExtractor.getPeriodType(item),
    fiscal_year: Number(
      periodExtractor.getFiscalYear(item)?.toString().slice(2)
    ),
  }))

  const { lastHistoricalPeriod } = getIsLastHistoricalPeriod(
    `all`,
    data.map((item) => ({
      period_type: periodExtractor.getPeriodType(item),
      id: periodExtractor.getPeriod(item),
    }))
  )

  return {
    dataGroupedByYear,
    years,
    months,
    availableYears,
    lastHistoricalPeriod,
  }
}

type BasicConfig = Record<
  string,
  {
    allowed_types: Partial<StandardInputType>[]
    type: StandardInputType
  }
>

export const getInputTypeProps = <T extends BasicConfig>(
  key: keyof T,
  config: T
): {
  allowedTypes: StandardInputType[]
  currentType: StandardInputType
} =>
  config && config[key]
    ? {
        allowedTypes: config[key].allowed_types,
        currentType: config[key].type,
      }
    : { allowedTypes: [`absolute`], currentType: `absolute` }

type GetPeriodTypeProps = {
  period: string
  historical: TimeframeSettings
  forecast: TimeframeSettings
}

export const getPeriodType = ({
  period,
  historical,
  forecast,
}: GetPeriodTypeProps): PeriodType => {
  if (!historical.isActive) {
    return `forecast`
  }
  if (!forecast.isActive) {
    return `historical`
  }
  if (historical.isActive && forecast.isActive) {
    const forecastDateStart = parseISOStringDate(forecast.startAt)

    if (period.split(`-`).length === 1) {
      // eslint-disable-next-line no-param-reassign
      period = `${period}-12`
    }
    const periodDate = parseISOStringDate(period)

    if (periodDate && forecastDateStart && periodDate >= forecastDateStart) {
      return `forecast`
    }

    return `historical`
  }
  return `historical`
}

export const getPeriodConfigType = <
  T extends Record<
    string,
    {
      type: StandardInputType
      allowed_types: StandardInputType[]
    }
  >,
>(
  field: keyof T,
  config: T
) => {
  const fieldConfig = config?.[field]
  if (!fieldConfig) return null
  if (!fieldConfig.type) return null
  const isAllowedType = fieldConfig.allowed_types.includes(fieldConfig.type)
  if (!isAllowedType) return null
  return fieldConfig.type
}

export const getPeriodConfigAllowedTypes = <
  T extends Record<
    string,
    {
      type: StandardInputType
      allowed_types: StandardInputType[]
    }
  >,
>(
  field: keyof T,
  config: T
) => {
  const fieldConfig = config?.[field]
  if (!fieldConfig) return [`absolute`] as StandardInputType[]
  if (!fieldConfig.allowed_types) return [`absolute`] as StandardInputType[]
  return fieldConfig.allowed_types
}
